'use client';

import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx, { ClassValue } from 'clsx';

import Button from '@/components/ui/button';
import Text, { TextRadius, TextStyle } from '@/components/ui/text-input';

interface GuestSelectionProps {
  value: string;
  label: string;
  name?: string;
  placeholder?: string;

  style?: TextStyle;
  radius?: TextRadius;

  onClear?(): void;
  onApply?(a?: string): void;
  onReset?(): void;
  unmount?: boolean;
  hasError?: boolean;
  button?: React.ReactNode;
  classNames?: Partial<{
    pannel: ClassValue;
    button: ClassValue;
    root: ClassValue;
    footer: ClassValue;
    input: ClassValue;
  }>;
  pos?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left';
  fixed?: boolean;
  id: string;
}

const PopoverDropdown: React.FC<PropsWithChildren<GuestSelectionProps>> = ({
  button,
  children,
  classNames,
  label,
  name,
  radius,
  style,
  value,
  onClear,
  onApply,
  pos,
  placeholder,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API compatibility; panel is always portaled
  fixed,
  hasError,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const showFooter = onClear && onApply;
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  openRef.current = open;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const el = triggerRef.current;
    const update = () => setTriggerRect(el.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const closeAndApply = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;
    setOpen(false);
    onApply?.();
  }, [onApply, id]);

  // Close when pointer/touch happens outside trigger and panel (capture phase).
  // We only listen for 'click' here so that on mobile a tap on the +/- buttons fires
  // touchstart first (we ignore it) then click; the click target is the button, so we
  // don't close. Listening for touchstart too caused premature close (backdrop or
  // hit-testing won before the button). Outside tap on mobile is handled by the
  // backdrop's onTouchStart.
  useEffect(() => {
    if (!open) return;
    const handle = (e: Event) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setTimeout(closeAndApply, 0);
    };
    document.addEventListener('click', handle, true);
    return () => {
      document.removeEventListener('click', handle, true);
    };
  }, [open, closeAndApply, id]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndApply();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeAndApply, id]);

  const panelClassName = clsx(
    'fixed z-[9999]',
    // Position is set via inline style for the portaled panel; avoid Tailwind top/bottom/left/right
    // so we don't stretch the panel (e.g. top-0 + bottom would fill the viewport).
    classNames?.pannel || 'flex -translate-x-1/2',
    'rounded-xl bg-white shadow-lg transition duration-200 ease-out',
  );

  const backdrop = (
    <div
      role="presentation"
      data-popover-backdrop
      data-testid="popover-backdrop"
      className="fixed inset-0 z-[9998] h-full w-dvw"
      style={{ touchAction: 'none' }}
      onClick={() => closeAndApply()}
      onTouchStart={(e) => {
        e.stopPropagation();
        closeAndApply();
      }}
    />
  );

  const panel = triggerRect && (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      className={panelClassName}
      style={{
        top: pos === 'top' ? undefined : triggerRect.bottom + 20,
        bottom: pos === 'top' ? window.innerHeight - triggerRect.top + 20 : undefined,
        left: triggerRect.left + triggerRect.width / 2,
        transform: 'translateX(-50%)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex w-full flex-col gap-[1rem]">
        <div>{children}</div>
        {showFooter && (
          <div
            className={clsx(
              'flex justify-between gap-[1rem] divide-x divide-gray-900/5 rounded-b-lg bg-gray-50 p-[1rem] pb-[0.75rem] pt-[1.5rem]',
              classNames?.footer
            )}
          >
            <Button
              style="text"
              size="small"
              className={clsx(
                typeof onClear !== 'function' && 'invisible',
                'w-fit text-gray-7 hover:!text-gray-9'
              )}
              type="button"
              onClick={() => {
                onClear && onClear();
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              type="button"
              style="secondary"
              onClick={() => closeAndApply()}
              className={clsx(
                typeof onApply !== 'function' && 'invisible',
                'w-fit'
              )}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const overlayPortal =
    typeof document !== 'undefined' &&
    open &&
    createPortal(
      <>
        {backdrop}
        {panel}
      </>,
      document.body
    );

  return (
    <div className={clsx('relative w-full', classNames?.root)}>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={clsx('h-full cursor-pointer p-0', classNames?.button)}
        onClick={() => {
          if (open) closeAndApply();
          else setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (open) closeAndApply();
            else setOpen(true);
          }
        }}
      >
        {button ? (
          button
        ) : (
          <Text
            id={id}
            hasError={hasError}
            style={style}
            radius={radius}
            readOnly
            label={label}
            value={value}
            placeholder={placeholder}
            name={`button-${name || label}`}
          />
        )}
      </div>

      {overlayPortal}
    </div>
  );
};

export default PopoverDropdown;
